import {type TaskChecklistsOption} from '@domain/hroc-checklists'
import {
  ShiftEventAndUpdateEvent,
  EquipmentIdType,
  Priority,
  TaskRepetition,
  EventType,
  User,
  BaseEvent,
  ShiftEvent,
  Task,
  useRequiredConfig
} from '@domain/shifthandover'
import {
  CategoryDropDown,
  PriorityDropDown,
  ProcessStageDropDown,
  EventProcessStageAutocomplete
} from '@hconnect/common/components/shiftEventFormFields'
import {EventHighlight} from '@hconnect/common/components/shiftEventFormFields/EventHighlight'
import {
  REGULAR_SIZE,
  INPUT_FORM_FIELD_SX,
  MAX_DESCRIPTION_CHARACTER_LIMIT
} from '@hconnect/common/consts'
import {isCustomRepetition} from '@hconnect/common/types'
import {removeMarkdownTags} from '@hconnect/common/utils'
import {
  shouldNotBeEmpty,
  shouldNotBeEmptyString,
  shouldNotBeEmptyStringWithMaxLength,
  ValidatorConfig
} from '@hconnect/common/validators'
import {useUpmPlantNow} from '@hconnect/upm'
import {Grid, Switch, TextField, Box} from '@mui/material'
import {EventDescriptionBox} from '@widgets/common-shift-event'
import {TaskChecklistsField} from '@widgets/hroc-checklists'
import _, {isArray} from 'lodash'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {RepetitionInputsSettings, ChecklistsInputSettings} from '../../types'
import {ShiftPicker} from '../dateTimeAndShift/ShiftPicker'
import {EquipmentSearchContainer} from '../eventProperties/EquipmentSearchContainer'
import {EventInfo} from '../eventProperties/EventInfo'
import {EventStatusDropDown} from '../eventProperties/EventStatus'
import {EventTypeDropdownBox} from '../eventProperties/EventTypeDropdownBox'
import {UserSearchField} from '../eventProperties/UserSearchField'
import {TaskRepetitionSettings} from '../repetitiveTask/TaskRepetitionSettings'

type TypeOfTask = Task & {
  checklists: TaskChecklistsOption[]
}

type PossibleErrors = keyof TypeOfTask | 'equipmentNumber'

export type Props = {
  plantId: string
  item: Partial<TypeOfTask>
  repetitionInputsSettings: RepetitionInputsSettings
  checklistsInputSettings: ChecklistsInputSettings
  allowCancellation: boolean
  update: <K extends keyof TypeOfTask>(key: K, value: TypeOfTask[K] | EventType) => void
  updateMultiPart: (delta) => void
  validationError: Map<PossibleErrors, string>
  editWithTaskSchedules: boolean
  disableDoNotStartBefore: boolean
  originalAssignees?: User[]
  equipmentIdType?: EquipmentIdType
  children: React.ReactNode
  autocompleteProcessStage?: boolean
}

// As the UI has to reflect the visible fields and knows best which of them is Required,
// the Validator config is put here to have both aligned at all time.

export const taskValidatorConfig: ValidatorConfig<ShiftEventAndUpdateEvent> = new Map([
  ['category', shouldNotBeEmpty<ShiftEventAndUpdateEvent>()],
  [
    'description',
    shouldNotBeEmptyStringWithMaxLength<ShiftEventAndUpdateEvent>(
      MAX_DESCRIPTION_CHARACTER_LIMIT,
      removeMarkdownTags
    )
  ],
  ['dueDate', shouldNotBeEmpty<ShiftEventAndUpdateEvent>()],
  ['status', shouldNotBeEmpty<ShiftEventAndUpdateEvent>()],
  ['title', shouldNotBeEmptyString<ShiftEventAndUpdateEvent>()]
])

export const taskDefaultObject = (): Partial<TypeOfTask> => {
  return {
    description: '',
    eventType: 'task',
    status: 'pending',
    title: '',
    checklists: []
  }
}

/**
 * if you consider using this component, check first src/container/EventFormContainer.tsx,
 * as it handles the validation and the stuff around this form,
 * it might already solve all your needs
 */
/* eslint-disable complexity */
export const TaskForm: React.FC<Props> = ({
  plantId,
  children,
  repetitionInputsSettings,
  checklistsInputSettings,
  allowCancellation,
  item,
  update,
  updateMultiPart,
  validationError,
  editWithTaskSchedules,
  originalAssignees = [],
  disableDoNotStartBefore,
  equipmentIdType,
  autocompleteProcessStage
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const {shifts, workOrderTarget} = useRequiredConfig(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const endOfToday = getPlantNow().endOf('day')
  const startOfToday = getPlantNow().startOf('day')

  const assignee = item.assignees || []
  const checklists = item.checklists || checklistsInputSettings.initialValue || []

  const dueDateHasError = typeof validationError.get('dueDate') === 'string'

  const getErrorText = (key: PossibleErrors) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  const updateTaskRepetition = useCallback(
    (next: TaskRepetition | null) => {
      if (repetitionInputsSettings.disableSelect && repetitionInputsSettings.disableCheckbox) return

      if (next) {
        update('priority', undefined)
      }

      update('repetitionInfo', next || null)
    },
    [update, repetitionInputsSettings.disableSelect, repetitionInputsSettings.disableCheckbox]
  )

  return (
    <>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          data-test-id="event-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('shiftEvent.label.title')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update('title', event.target.value)
          }
          required
          value={item.title}
          variant="filled"
          InputProps={INPUT_FORM_FIELD_SX}
        />
        <EventHighlight
          title={t('eventsTable.action.highlight')}
          value={item.isHighlighted}
          onChange={(value) => update('isHighlighted', value)}
        />
      </Box>

      {'created' in item && (
        <EventInfo
          item={item as Partial<ShiftEvent>}
          plantId={plantId}
          workOrderTarget={workOrderTarget}
        />
      )}

      <Grid container my={0.5} spacing={2} alignItems="center">
        <Grid item {...REGULAR_SIZE}>
          <EventTypeDropdownBox
            item={item as BaseEvent}
            value="task"
            updateMultiPart={updateMultiPart}
            disabled={'repetitionInfo' in item && item.repetitionInfo !== null}
          />
        </Grid>

        <Grid item {...REGULAR_SIZE}>
          <EventStatusDropDown
            translate={t}
            data-test-id="event-form-status"
            errorText={getErrorText('status')}
            onChange={(value) => update('status', value)}
            required
            allow={allowCancellation ? ['cancelled'] : undefined}
            value={item.status}
            disabled={editWithTaskSchedules}
          />
        </Grid>
        <Grid item {...REGULAR_SIZE}>
          <CategoryDropDown
            translate={t}
            data-test-id="event-form-category"
            errorText={getErrorText('category')}
            onChange={(value) => update('category', value)}
            required
            value={item.category}
          />
        </Grid>
        <Grid item {...REGULAR_SIZE}>
          <PriorityDropDown
            translate={t}
            data-test-id="event-form-priority"
            errorText={getErrorText('priority')}
            onChange={(value) => update('priority', value)}
            value={item.priority as Priority}
          />
        </Grid>

        <Grid item xs={12}>
          {/* Note: Due date can be changed while editing Single task from custom series */}
          {(repetitionInputsSettings.disableSelect ||
            !(item.repetitionInfo && isCustomRepetition(item.repetitionInfo))) && (
            <ShiftPicker
              plantId={plantId}
              shifts={shifts}
              error={dueDateHasError}
              label={<Box>{t('shiftEvent.label.dueDate')} *</Box>}
              localDate={item.dueDate || endOfToday}
              localStartDate={item.doNotStartBefore}
              enableSelectRange={!disableDoNotStartBefore}
              onChange={(pickedShift) => {
                if (isArray(pickedShift)) {
                  const [start, end] = pickedShift
                  update('doNotStartBefore', start.endDate)
                  update('dueDate', end.endDate)
                  update('repetitionInfo', null)
                } else {
                  update('dueDate', pickedShift.endDate)
                  update('doNotStartBefore', undefined)
                }
              }}
            />
          )}
        </Grid>
        <Grid container m={0} alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <UserSearchField
              plantId={plantId}
              label={`${t('shiftEvent.label.assignee')} (${t('shiftEvent.label.optional')})`}
              errorText={getErrorText('assignees')}
              data-test-id="assigneeSearch"
              onChange={(newAssignee) => {
                if (newAssignee && !_.isEqual(newAssignee, assignee)) {
                  const disableToggle = newAssignee.every((newVal) =>
                    originalAssignees.some((oldVal) => newVal.id === oldVal.id)
                  )
                  updateMultiPart({
                    assignees: newAssignee,
                    toggleAssigneeNotification: !disableToggle
                  })
                } else if (!newAssignee) {
                  updateMultiPart({
                    assignees: []
                  })
                }
              }}
              value={assignee}
            />
          </Grid>
          {'toggleAssigneeNotification' in item && (
            <Box pl={2}>
              {t('shiftEvent.action.assigneeNotificationToggle')}{' '}
              <Switch
                checked={item.toggleAssigneeNotification}
                data-test-id="events-notify-assignee-toggle"
                onChange={() =>
                  update('toggleAssigneeNotification', !item.toggleAssigneeNotification)
                }
                color="primary"
              />
            </Box>
          )}
        </Grid>

        <TaskRepetitionSettings
          plantId={plantId}
          repetitionInputsSettings={repetitionInputsSettings}
          onChange={updateTaskRepetition}
          settings={item.repetitionInfo || null}
          gridSetting={REGULAR_SIZE}
          defaultStartDate={
            // if due date is a valid date in the future, then start date should be due date for custom repetition
            // Start date is not required for quick repetition types
            item.dueDate && startOfToday.isBefore(item.dueDate) ? item.dueDate : endOfToday
          }
        />

        <Grid item xs={12}>
          <EquipmentSearchContainer
            plantId={plantId}
            data-test-id="event-form-equipmentNumber"
            errorText={getErrorText('equipmentNumber')}
            updateMultiPart={updateMultiPart}
            value={item as BaseEvent}
            equipmentIdType={equipmentIdType}
          />
        </Grid>
        {autocompleteProcessStage ? (
          <Grid item xs={12}>
            <EventProcessStageAutocomplete
              plantId={plantId}
              data-test-id="event-form-processStage"
              errorText={getErrorText('processStage')}
              onChange={(value?: string) => update('processStage', value)}
              value={item.processStage}
            />
          </Grid>
        ) : (
          <Grid item {...REGULAR_SIZE}>
            <ProcessStageDropDown
              translate={t}
              plantId={plantId}
              data-test-id="event-form-processStage"
              errorText={getErrorText('processStage')}
              onChange={(value) => update('processStage', value)}
              value={item.processStage}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <EventDescriptionBox
            current={item.description || ''}
            errorCode={validationError.get('description')}
            onChange={(next) => update('description', next)}
            required
          />
        </Grid>

        {checklistsInputSettings.showChecklists && (
          <Grid item xs={12}>
            <TaskChecklistsField
              plantId={plantId}
              errorText={validationError.get('checklists')}
              onChange={(next) => update('checklists', next)}
              value={checklists}
              disabled={checklistsInputSettings.disableSelect}
            />
          </Grid>
        )}
      </Grid>
      {children}
    </>
  )
}
